import { Shield } from "lucide-react";

export default function Privacy() {
  return (
    <main data-testid="privacy-page" className="pt-24 md:pt-32 pb-20">
      <div className="max-w-screen-md mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-400 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs font-medium text-purple-400 uppercase tracking-widest">
              Documento Legale
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-purple-200/50">
            Ultimo aggiornamento: Gennaio 2026
          </p>
        </div>

        {/* Content */}
        <div className="space-y-10">
          <section className="legal-section" data-testid="privacy-intro">
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-2xl p-6">
              <p className="text-purple-200/80 leading-relaxed">
                La tua privacy è importante per noi. La presente Privacy Policy descrive come vengono raccolti, 
                utilizzati, condivisi e protetti i dati personali degli utenti che utilizzano l'app mobile 
                Nerbys (il "Servizio"), in conformità al Regolamento (UE) 2016/679 ("GDPR").
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-titolare">
            <h2 className="text-2xl font-bold text-white mb-4">
              1. Titolare del trattamento
            </h2>
            <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-6">
              <p className="text-white font-semibold mb-2">Giuseppe D'Ambra</p>
              <p className="text-purple-200/70 mb-3">Titolare e sviluppatore del Servizio ("Nerbys" o "noi")</p>
              <div className="space-y-1">
                <p className="text-purple-200/70">
                  <strong className="text-white">Email privacy:</strong> <a href="mailto:privacy@nerbys.app" className="text-purple-400 hover:underline">privacy@nerbys.app</a>
                </p>
                <p className="text-purple-200/70">
                  <strong className="text-white">Email supporto:</strong> <a href="mailto:support@nerbys.app" className="text-purple-400 hover:underline">support@nerbys.app</a>
                </p>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-data-collection">
            <h2 className="text-2xl font-bold text-white mb-4">
              2. Dati personali trattati
            </h2>
            <div className="space-y-6 text-purple-200/70 text-sm leading-relaxed">
              <div>
                <p className="text-white font-semibold mb-3">2.1 Dati forniti dall'utente</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Email e password (registrazione e accesso).</li>
                  <li>Nickname, foto profilo (1 principale + fino a 3 aggiuntive).</li>
                  <li>Bio, genere, segni distintivi (facoltativi).</li>
                  <li>Link a profili social esterni (es. Instagram, Facebook, TikTok, X, LinkedIn) – almeno uno richiesto per utilizzare la funzione di scansione/proximity.</li>
                  <li>Stato temporaneo "In questo momento" (scade automaticamente dopo 4 ore).</li>
                </ul>
                <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-4 mt-4">
                  <p className="text-purple-300">
                    <strong>Importante sui social link:</strong> Nerbys tratta il link/identificativo del profilo indicato dall'utente. 
                    Nerbys non richiede né raccoglie le credenziali di accesso ai social e non accede ai contenuti tramite autenticazione.
                  </p>
                </div>
              </div>
              
              <div>
                <p className="text-white font-semibold mb-3">2.2 Dati trattati automaticamente</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Dati di posizione (coordinate GPS) per la funzionalità proximity (vedi §3.2).</li>
                  <li>Token dispositivo per notifiche push.</li>
                  <li>Data e ora di registrazione, ultimo accesso e informazioni tecniche essenziali (log).</li>
                  <li>Identificatori tecnici del dispositivo (es. informazioni su device/OS e identificativi necessari a sicurezza e funzionamento).</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-finalita">
            <h2 className="text-2xl font-bold text-white mb-4">
              3. Finalità del trattamento e basi giuridiche
            </h2>
            <div className="space-y-6 text-purple-200/70 text-sm leading-relaxed">
              <p>Trattiamo i dati personali per le seguenti finalità:</p>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-5">
                <p className="text-white font-semibold mb-2">3.1 Creazione e gestione dell'account; erogazione del Servizio</p>
                <p><strong className="text-purple-300">Dati:</strong> email, password, nickname, foto profilo, bio, link social, log tecnici essenziali.</p>
                <p><strong className="text-purple-300">Base giuridica:</strong> esecuzione del contratto/fornitura del Servizio (art. 6(1)(b) GDPR).</p>
              </div>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-5">
                <p className="text-white font-semibold mb-2">3.2 Funzionalità di social discovery basata sulla prossimità (raggio indicativo 50 metri)</p>
                <p><strong className="text-purple-300">Dati:</strong> posizione (GPS) e dati tecnici connessi alla funzionalità.</p>
                <p><strong className="text-purple-300">Base giuridica:</strong> esecuzione del contratto/fornitura del Servizio (art. 6(1)(b) GDPR).</p>
                <div className="mt-4 space-y-2">
                  <p><strong className="text-white">Posizione durante l'uso e in background:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong className="text-white">Durante l'uso:</strong> quando l'utente apre l'app e utilizza la scansione, la posizione viene trattata per individuare utenti nelle vicinanze.</li>
                    <li><strong className="text-white">In background (se autorizzato):</strong> se l'utente concede i permessi di localizzazione in background, il Servizio può trattare la posizione anche quando l'app non è in primo piano.</li>
                    <li><strong className="text-white">Controllo dell'utente:</strong> l'utente può in qualsiasi momento limitare o revocare i permessi di localizzazione dalle impostazioni del dispositivo.</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-5">
                <p className="text-white font-semibold mb-2">3.3 Notifiche push</p>
                <p><strong className="text-purple-300">Dati:</strong> token push, eventi legati alle richieste/connessioni.</p>
                <p><strong className="text-purple-300">Base giuridica:</strong> esecuzione del contratto (art. 6(1)(b) GDPR). Le notifiche possono essere disattivate dalle impostazioni del dispositivo.</p>
              </div>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-5">
                <p className="text-white font-semibold mb-2">3.4 Sicurezza, prevenzione abusi, applicazione delle regole</p>
                <p><strong className="text-purple-300">Dati:</strong> log di sicurezza, identificativi tecnici, informazioni relative a segnalazioni/blocchi e provvedimenti.</p>
                <p><strong className="text-purple-300">Base giuridica:</strong> legittimo interesse (art. 6(1)(f) GDPR).</p>
              </div>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-5">
                <p className="text-white font-semibold mb-2">3.5 Verifica automatizzata della foto profilo (AI "umano/non umano")</p>
                <p><strong className="text-purple-300">Dati:</strong> foto profilo caricata dall'utente; esito/indicatore tecnico del controllo.</p>
                <p><strong className="text-purple-300">Finalità:</strong> ridurre profili non autentici e migliorare la sicurezza.</p>
                <p><strong className="text-purple-300">Base giuridica:</strong> legittimo interesse (art. 6(1)(f) GDPR).</p>
                <p className="mt-3 text-purple-300">
                  <strong>Precisazione:</strong> la verifica è finalizzata a controllare che la foto profilo raffiguri un soggetto umano. 
                  Non è utilizzata per advertising o identificazione univoca. In caso di rifiuto, l'utente può sostituire la foto o 
                  richiedere riesame contattando support@nerbys.app.
                </p>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-infrastruttura">
            <h2 className="text-2xl font-bold text-white mb-4">
              4. Modalità del trattamento e infrastruttura tecnica
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>Nerbys tratta i dati mediante strumenti informatici e misure tecniche e organizzative adeguate.</p>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-6">
                <p className="text-white font-semibold mb-3">Architettura (alto livello):</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-white">Backend:</strong> server Python (FastAPI) su infrastruttura cloud.</li>
                  <li><strong className="text-white">Database:</strong> MongoDB per dati di account, profili e connessioni.</li>
                  <li><strong className="text-white">Autenticazione:</strong> JWT per sessioni.</li>
                  <li><strong className="text-white">Storage media:</strong> archiviazione sicura delle foto profilo (con misure di protezione).</li>
                  <li><strong className="text-white">Geolocalizzazione:</strong> elaborazione coordinate GPS per funzionalità proximity.</li>
                </ul>
                <p className="mt-4 text-purple-400 text-xs">
                  (Per ragioni di sicurezza non pubblichiamo dettagli che possano facilitare attacchi; 
                  informazioni aggiuntive possono essere fornite su richiesta alle autorità competenti.)
                </p>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-conservazione">
            <h2 className="text-2xl font-bold text-white mb-4">
              5. Conservazione dei dati
            </h2>
            <div className="space-y-2 text-purple-200/70 text-sm leading-relaxed">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Dati di account e profilo:</strong> fino alla cancellazione dell'account da parte dell'utente.</li>
                <li><strong className="text-white">Dati di posizione:</strong> conservati per un massimo di 1 ora dall'ultimo aggiornamento, poi eliminati automaticamente.</li>
                <li><strong className="text-white">Log di sicurezza/anti-abuso:</strong> fino a 12 mesi, salvo ulteriore conservazione per obblighi di legge.</li>
                <li><strong className="text-white">Stato "In questo momento":</strong> scade automaticamente dopo 4 ore.</li>
                <li><strong className="text-white">Foto profilo:</strong> eliminate entro 30 giorni dalla cancellazione dell'account.</li>
                <li><strong className="text-white">Backup:</strong> copie temporanee per finalità di ripristino e sicurezza; i dati vengono eliminati secondo tempi tecnici di sovrascrittura.</li>
              </ul>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-condivisione">
            <h2 className="text-2xl font-bold text-white mb-4">
              6. Condivisione dei dati e destinatari
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                <p className="text-green-400 font-semibold">I dati personali non vengono venduti a terzi.</p>
              </div>
              <p>Possiamo condividere dati con:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Altri utenti del Servizio</strong> (in base alle impostazioni Pubblico/Privato e alle funzionalità).</li>
                <li><strong className="text-white">Fornitori di servizi tecnici</strong> (es. hosting cloud, notifiche push, strumenti di moderazione), nominati Responsabili del trattamento ai sensi dell'art. 28 GDPR.</li>
                <li><strong className="text-white">Autorità competenti</strong> quando richiesto dalla legge o necessario per tutelare diritti, sicurezza e prevenzione abusi.</li>
              </ul>
              <p className="mt-4">
                Su richiesta a <a href="mailto:privacy@nerbys.app" className="text-purple-400 hover:underline">privacy@nerbys.app</a> è 
                disponibile un elenco aggiornato delle categorie di fornitori/Responsabili utilizzati.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-trasferimenti">
            <h2 className="text-2xl font-bold text-white mb-4">
              7. Trasferimenti verso Paesi extra SEE
            </h2>
            <div className="text-purple-200/70 text-sm leading-relaxed">
              <p>
                Qualora alcuni fornitori trattino dati al di fuori dello Spazio Economico Europeo, 
                il trasferimento avverrà nel rispetto del GDPR mediante garanzie adeguate 
                (ad es. Clausole Contrattuali Standard e, se del caso, misure supplementari).
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-diritti">
            <h2 className="text-2xl font-bold text-white mb-4">
              8. Diritti dell'utente
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                Ai sensi del GDPR, l'utente può esercitare i diritti di <strong className="text-white">accesso</strong>, 
                <strong className="text-white"> rettifica</strong>, <strong className="text-white">cancellazione</strong>, 
                <strong className="text-white"> limitazione</strong>, <strong className="text-white">portabilità</strong>, 
                <strong className="text-white"> opposizione</strong> e, ove applicabile, <strong className="text-white">revoca del consenso</strong>.
              </p>
              <p>
                Per esercitare i diritti: <a href="mailto:privacy@nerbys.app" className="text-purple-400 hover:underline">privacy@nerbys.app</a>. 
                Risponderemo entro 30 giorni (salvo proroghe nei casi consentiti dal GDPR).
              </p>
              <p>
                L'utente ha inoltre il diritto di proporre reclamo al <strong className="text-white">Garante per la protezione dei dati personali</strong>.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-cancellazione">
            <h2 className="text-2xl font-bold text-white mb-4">
              9. Cancellazione dell'account
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>L'utente può cancellare l'account:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>direttamente dall'App, tramite la funzione "Elimina account" nelle impostazioni;</li>
                <li>e/o tramite richiesta a <a href="mailto:privacy@nerbys.app" className="text-purple-400 hover:underline">privacy@nerbys.app</a>;</li>
                <li>e/o tramite la pagina web dedicata.</li>
              </ul>
              <p className="mt-4">
                La cancellazione comporta la rimozione del profilo dalla discovery e l'eliminazione dei dati 
                dai sistemi attivi, con le eccezioni/tempi tecnici e di sicurezza indicati in questa Privacy Policy.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-sicurezza">
            <h2 className="text-2xl font-bold text-white mb-4">
              10. Misure di sicurezza
            </h2>
            <div className="space-y-2 text-purple-200/70 text-sm leading-relaxed">
              <p>Adottiamo misure adeguate per proteggere i dati personali, tra cui:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>password memorizzate con hashing sicuro (es. bcrypt);</li>
                <li>connessioni cifrate (HTTPS/TLS);</li>
                <li>controllo accessi ai sistemi, monitoraggio e logging;</li>
                <li>sistemi di segnalazione e blocco utenti e misure anti-abuso;</li>
                <li>backup protetti e procedure di ripristino.</li>
              </ul>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-minori">
            <h2 className="text-2xl font-bold text-white mb-4">
              11. Minori
            </h2>
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
              <p className="text-yellow-400/90 text-sm">
                <strong>Il Servizio è destinato a utenti di età pari o superiore a 17 anni.</strong><br/>
                Non intendiamo raccogliere consapevolmente dati personali di minori al di sotto di tale età. 
                Se venissimo a conoscenza di aver raccolto dati di un minore, adotteremo misure ragionevoli 
                per rimuoverli e chiudere l'account.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-modifiche">
            <h2 className="text-2xl font-bold text-white mb-4">
              12. Modifiche alla Privacy Policy
            </h2>
            <div className="text-purple-200/70 text-sm leading-relaxed">
              <p>
                Eventuali modifiche significative saranno comunicate tramite app e/o email con un preavviso 
                ragionevole (indicativamente 30 giorni) e pubblicate su questa pagina. 
                La data di "ultimo aggiornamento" indica la versione vigente.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="privacy-contact">
            <h2 className="text-2xl font-bold text-white mb-4">
              13. Contatti
            </h2>
            <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-6">
              <p className="text-white font-bold mb-3">Nerbys – Giuseppe D'Ambra</p>
              <div className="space-y-2">
                <p className="text-purple-200/70">
                  <strong className="text-white">Privacy:</strong> <a href="mailto:privacy@nerbys.app" className="text-purple-400 hover:underline">privacy@nerbys.app</a>
                </p>
                <p className="text-purple-200/70">
                  <strong className="text-white">Supporto:</strong> <a href="mailto:support@nerbys.app" className="text-purple-400 hover:underline">support@nerbys.app</a>
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
